import { MESSAGE_TYPE } from 'shared/constants/messages';
import { applyPageFilters, applyRoleFilter, sortComparator } from './helpers';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { matchesFilters } from './helpers/filterHelpers';
import {
  getUserPermissions,
  getUserRole,
} from '../../../helper/permissionsHelper';
import camelcaseKeys from 'camelcase-keys';

const resolveRoleContext = rootGetters => {
  const currentUser = rootGetters?.getCurrentUser || {};
  const currentUserId = currentUser?.id;
  const currentAccountId = rootGetters?.getCurrentAccountId;
  const hasRoleContext = Boolean(currentUserId && currentAccountId != null);

  if (!hasRoleContext) {
    return {
      currentUser,
      currentUserId,
      currentAccountId,
      permissions: [],
      userRole: 'administrator',
      hasRoleContext: false,
    };
  }

  return {
    currentUser,
    currentUserId,
    currentAccountId,
    permissions: getUserPermissions(currentUser, currentAccountId),
    userRole: getUserRole(currentUser, currentAccountId),
    hasRoleContext: true,
  };
};

const getConversationParticipants = conversation =>
  conversation?.meta?.participants ||
  conversation?.participants ||
  conversation?.meta?.conversation_participants;

const isConversationParticipant = (conversation, currentUserId) => {
  const participants = getConversationParticipants(conversation) || [];
  return participants.some(participant => participant?.id === currentUserId);
};

export const getSelectedChatConversation = ({
  allConversations,
  selectedChatId,
}) =>
  allConversations.filter(conversation => conversation.id === selectedChatId);

const getters = {
  getAllConversations: ({ allConversations, chatSortFilter: sortKey }) => {
    return allConversations.sort((a, b) => sortComparator(a, b, sortKey));
  },
  getFilteredConversations: (
    { allConversations, chatSortFilter, appliedFilters },
    _,
    __,
    rootGetters
  ) => {
    const { currentUserId, permissions, userRole, hasRoleContext } =
      resolveRoleContext(rootGetters);

    return allConversations
      .filter(conversation => {
        const matchesFilterResult = matchesFilters(
          conversation,
          appliedFilters
        );
        const allowedForRole = hasRoleContext
          ? applyRoleFilter(conversation, userRole, permissions, currentUserId)
          : true;

        return matchesFilterResult && allowedForRole;
      })
      .sort((a, b) => sortComparator(a, b, chatSortFilter));
  },
  getSelectedChat: ({ selectedChatId, allConversations }) => {
    const selectedChat = allConversations.find(
      conversation => conversation.id === selectedChatId
    );
    return selectedChat || {};
  },
  getSelectedChatAttachments: ({ selectedChatId, attachments }) => {
    return attachments[selectedChatId] || [];
  },
  getSelectedChatAttachmentsLoaded: ({ selectedChatId, attachments }) =>
    selectedChatId !== null && attachments[selectedChatId] !== undefined,
  getChatListFilters: ({ conversationFilters }) => conversationFilters,
  getLastEmailInSelectedChat: (stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;
    const lastEmail = [...messages].reverse().find(message => {
      const { message_type: messageType } = message;
      if (message.private) return false;

      return [MESSAGE_TYPE.OUTGOING, MESSAGE_TYPE.INCOMING].includes(
        messageType
      );
    });

    return lastEmail;
  },
  getMineChats: (_state, _, __, rootGetters) => activeFilters => {
    const { currentUserId, permissions, userRole, hasRoleContext } =
      resolveRoleContext(rootGetters);

    return _state.allConversations.filter(conversation => {
      const { assignee } = conversation.meta;
      const isAssignedToMe = assignee && assignee.id === currentUserId;
      const hasParticipatingManage = permissions.includes(
        'conversation_participating_manage'
      );
      const hasParticipantData = Array.isArray(
        getConversationParticipants(conversation)
      );
      const isParticipant = isConversationParticipant(
        conversation,
        currentUserId
      );
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      const allowedForRole = hasRoleContext
        ? applyRoleFilter(conversation, userRole, permissions, currentUserId)
        : true;
      const shouldTrustBackendForParticipating =
        hasParticipatingManage && !hasParticipantData;
      const isChatMine =
        (isAssignedToMe ||
          isParticipant ||
          shouldTrustBackendForParticipating) &&
        shouldFilter &&
        allowedForRole;

      return isChatMine;
    });
  },
  getAppliedConversationFiltersV2: _state => {
    // TODO: Replace existing one with V2 after migrating the filters to use camelcase
    return _state.appliedFilters.map(camelcaseKeys);
  },
  getAppliedConversationFilters: _state => {
    return _state.appliedFilters;
  },
  getAppliedConversationFiltersQuery: _state => {
    const hasAppliedFilters = _state.appliedFilters.length !== 0;
    return hasAppliedFilters ? filterQueryGenerator(_state.appliedFilters) : [];
  },
  getUnAssignedChats: (_state, _, __, rootGetters) => activeFilters => {
    const { currentUserId, permissions, userRole, hasRoleContext } =
      resolveRoleContext(rootGetters);

    return _state.allConversations.filter(conversation => {
      const isUnAssigned = !conversation.meta.assignee;
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      const allowedForRole = hasRoleContext
        ? applyRoleFilter(conversation, userRole, permissions, currentUserId)
        : true;
      return isUnAssigned && shouldFilter && allowedForRole;
    });
  },
  getParticipatingChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserId = rootGetters.getCurrentUser?.id;
    const getWatchers = rootGetters['conversationWatchers/getByConversationId'];
    return _state.allConversations.filter(conversation => {
      const watchers = getWatchers(conversation.id);
      // Watchers are only loaded for the conversation open in the detail
      // panel. If loaded and current user is not in them, filter it out.
      if (watchers && !watchers.some(w => w.id === currentUserId)) {
        return false;
      }
      return applyPageFilters(conversation, activeFilters);
    });
  },
  getAllStatusChats: (_state, _, __, rootGetters) => activeFilters => {
    const { currentUserId, permissions, userRole, hasRoleContext } =
      resolveRoleContext(rootGetters);

    return _state.allConversations.filter(conversation => {
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      const allowedForRole = hasRoleContext
        ? applyRoleFilter(conversation, userRole, permissions, currentUserId)
        : true;

      return shouldFilter && allowedForRole;
    });
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getAllMessagesLoaded(_state) {
    const [chat] = getSelectedChatConversation(_state);
    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return [];
    return chat.messages.filter(
      chatMessage =>
        chatMessage.created_at * 1000 > chat.agent_last_seen_at * 1000 &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getConversationById: _state => conversationId => {
    return _state.allConversations.find(
      value => value.id === Number(conversationId)
    );
  },
  getConversationParticipants: _state => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: _state => {
    return _state.conversationLastSeen;
  },

  getContextMenuChatId: _state => {
    return _state.contextMenuChatId;
  },

  getCopilotAssistant: _state => {
    return _state.copilotAssistant;
  },
};

export default getters;
